import {useEffect, useMemo, useState} from 'react';
import cn from 'clsx';
import {useTranslation} from 'react-i18next';
import {Controller, useFormContext} from 'react-hook-form';
import SeasonEntity from '../../../entities/taxes/season.entity';
import {FormTypes} from '../types';
import {FORM_NAMES} from '../constants';
import {GuestGroup} from '@chekin/core';
import {useReservation} from 'context/reservation';
import {getFields, getLabels} from '../utils';
import {Button} from '@chekin/guestapp-ui-kit';
import CounterStepper from 'components/common/CounterStepper';

type NumberOfGuestsFormProps = {
  isLoading: boolean;
  seasonEntity: SeasonEntity;
  ageLimit?: number | null;
  guestGroup?: GuestGroup;
  disabled?: boolean;
  onSubmit: () => void;
  canEdit?: boolean | null;
  className?: string;
};

function NumberOfGuestsForm({
  ageLimit,
  guestGroup,
  isLoading,
  disabled,
  onSubmit,
  canEdit,
  seasonEntity,
  className,
}: NumberOfGuestsFormProps) {
  const {t} = useTranslation();
  const {control, handleSubmit, watch, reset, getValues} = useFormContext<FormTypes>();
  const {allowEditingGuestNumber} = useReservation();
  const [numberOfGuests, setNumberOfGuests] = useState(0);
  const totalJuniors = watch(FORM_NAMES.junior);
  const totalMiddle = watch(FORM_NAMES.middle);
  const totalSeniors = watch(FORM_NAMES.senior);

  const visibleFields = useMemo(
    () =>
      getFields({
        lessThanRule: seasonEntity.lessThanRule,
        betweenRule: seasonEntity.betweenRule,
        moreThanRule: seasonEntity.moreThanRule,
      }),
    [seasonEntity],
  );

  const labels = getLabels({
    lessThanRule: seasonEntity.lessThanRule,
    betweenRule: seasonEntity.betweenRule,
    moreThanRule: seasonEntity.moreThanRule,
    visibleFields,
    t,
  });

  useEffect(
    function preload() {
      if (typeof guestGroup?.known_number_of_guests !== 'number') return;
      const isNotDefinedAnyGroup =
        !guestGroup?.children && !guestGroup?.adults && !guestGroup?.seniors;
      const currentValues = getValues();
      if (isNotDefinedAnyGroup) {
        reset({
          ...currentValues,
          [FORM_NAMES.numberOfGuests]: guestGroup?.known_number_of_guests ?? 0,
          [FORM_NAMES.junior]: 0,
          [FORM_NAMES.middle]: visibleFields[FORM_NAMES.middle]
            ? guestGroup?.known_number_of_guests ?? 0
            : 0,
          [FORM_NAMES.senior]: !visibleFields[FORM_NAMES.middle]
            ? guestGroup?.known_number_of_guests ?? 0
            : 0,
        });
        return;
      }
      reset({
        ...currentValues,
        [FORM_NAMES.numberOfGuests]: guestGroup?.known_number_of_guests ?? 0,
        [FORM_NAMES.junior]: guestGroup?.children ?? 0,
        [FORM_NAMES.middle]: guestGroup?.adults ?? 0,
        [FORM_NAMES.senior]: guestGroup?.seniors ?? 0,
      });
    },
    [
      guestGroup?.adults,
      guestGroup?.seniors,
      guestGroup?.children,
      guestGroup?.known_number_of_guests,
      visibleFields,
      reset,
      getValues,
    ],
  );

  useEffect(
    function updateNumberOfGuest() {
      setNumberOfGuests(totalJuniors + totalMiddle + totalSeniors);
    },
    [totalJuniors, totalMiddle, totalSeniors],
  );

  const isDisabled = disabled || isLoading;

  const isLimitOfGuestsExceeded =
    !allowEditingGuestNumber &&
    !!seasonEntity &&
    Boolean((guestGroup?.known_number_of_guests ?? 0) < numberOfGuests);

  const fieldsNumberClassName =
    Object.keys(visibleFields).length > 1 ? 'multiple-fields' : 'single-field';

  return (
    <div className={cn(className, fieldsNumberClassName, 'kiosko:mt-6')}>
      {visibleFields[FORM_NAMES.numberOfGuests] && (
        <Controller
          name={FORM_NAMES.numberOfGuests}
          control={control}
          render={({field, fieldState: {error}}) => {
            return (
              <CounterStepper
                title={t('number_of_guests')}
                subtitle={ageLimit && t('age_limit_years_old', {ageLimit})}
                error={error?.message}
                disabled={isDisabled}
                readOnly={!canEdit}
                {...field}
              />
            );
          }}
        />
      )}
      {visibleFields[FORM_NAMES.junior] && (
        <Controller
          name={FORM_NAMES.junior}
          control={control}
          render={({field, fieldState: {error}}) => {
            return (
              <CounterStepper
                title={labels.junior}
                disabled={isDisabled}
                readOnly={!canEdit}
                error={error?.message}
                {...field}
              />
            );
          }}
        />
      )}
      {visibleFields[FORM_NAMES.middle] && (
        <Controller
          name={FORM_NAMES.middle}
          control={control}
          render={({field, fieldState: {error}}) => {
            return (
              <CounterStepper
                title={labels.middle}
                disabled={isDisabled}
                readOnly={!canEdit}
                error={error?.message}
                {...field}
              />
            );
          }}
        />
      )}
      {visibleFields[FORM_NAMES.senior] && (
        <Controller
          name={FORM_NAMES.senior}
          control={control}
          render={({field, fieldState: {error}}) => {
            return (
              <CounterStepper
                title={labels.senior}
                error={error?.message}
                readOnly={!canEdit}
                disabled={isDisabled}
                {...field}
              />
            );
          }}
        />
      )}

      <Button
        disabled={disabled || isLoading || isLimitOfGuestsExceeded}
        onClick={handleSubmit(onSubmit)}
        className={cn(
          'number-of-guests-form__button',
          'mt-[30px] w-full mx-auto max-w-[295px] flex',
          'kiosko:mt-14 kiosko:max-w-full',
        )}
      >
        {t('confirm')}
      </Button>
    </div>
  );
}

export {NumberOfGuestsForm};
